<template>
  <div class="px-4 pb-4 sm:px-6 sm:pb-6">
    <h1 class="my-2">{{ t("headings.tasks") }}</h1>
    <breadcrumb class="mt-3 mb-4" />
    <div class="card">
      <custom-table
        :fields="fields"
        :items="tasks"
        :loading="waiting"
        :pagination="true"
        :perPage="10"
        :searchable="true"
        :searchFields="searchFields"
        :showHeader="true"
        sortBy="startDate"
        :ascending="false"
        :sortFields="sortFields"
        @row-click="(id) => router.push(`/tasks/${id}`)"
      >
        <template #header>
          <span class="flex justify-end"
            ><form-button
              :command="() => router.push('/tasks/create')"
              :icon="PlusIcon"
              id="header-task-create-button"
              label="create_task"
              :textVariant="company.primaryText"
              type="button"
              :variant="company.primary"
          /></span>
        </template>
        <template #cell(requestId)="{ value, item }">
          <div v-if="value">
            <router-link
              :to="`/notifications/${value}`"
              class="underline hover:text-blue-400"
              >{{
                item.notification && item.notification.estate
                  ? item.notification.estate.name +
                    " " +
                    item.notification.apartment
                  : null
              }}</router-link
            >
          </div>
          <div v-else>
            <router-link
              :to="`/projects/${item.projectId}`"
              class="underline hover:text-blue-400"
              >{{
                item.project && item.project.name ? item.project.name : null
              }}</router-link
            >
          </div>
        </template>
        <template #cell(estateId)="{ value }">
          {{ getEstateName(value, estates) }}
        </template>
        <template #cell(startDate)="{ value }">
          {{ getLocaleDate(locale, value) }}
        </template>
        <template #cell(endDate)="{ value, item }">
          <span
            :class="{ 'text-rose-600': closeToDeadline(value, item.status) }"
            >{{ getLocaleDate(locale, value) }}</span
          >
        </template>
        <template #cell(status)="{ value }">
          {{ t("status." + value) }}
        </template>
      </custom-table>
    </div>
  </div>
</template>

<script>
import { KeyIcon, PlusIcon } from "@heroicons/vue/24/solid";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import Breadcrumb from "../components/breadcrumb/Breadcrumb.vue";
import FormButton from "../components/form/FormButton.vue";
import CustomTable from "../components/table/CustomTable.vue";
import { closeToDeadline, getLocaleDate } from "../utils/dateUtils";
import { getEstateName } from "../utils/estateUtil";

export default {
  components: { Breadcrumb, CustomTable, FormButton, KeyIcon, PlusIcon },
  setup() {
    const router = useRouter();
    const store = useStore();
    const { locale, t } = useI18n();

    const company = computed(() => store.state.company.company);
    const estates = computed(() => store.state.estate.estates);
    const tasks = computed(() => store.state.task.tasks);
    const user = computed(() => store.state.authentication.user);

    const waiting = computed(() => {
      if (store.state.authentication.waiting) return true;
      if (store.state.company.waiting) return true;
      if (store.state.estate.waiting) return true;
      if (store.state.task.waiting) return true;
      return false;
    });

    const fields = ref([
      {
        key: "name",
        label: "name",
      },
      {
        key: "requestId",
        label: "project_request",
      },
      {
        key: "estateId",
        label: "estate_name",
      },
      {
        key: "startDate",
        label: "start_date",
      },
      {
        key: "endDate",
        label: "end_date",
      },
      {
        key: "status",
        label: "status",
      },
    ]);
    const searchFields = ref(["name", "startDate", "endDate", "status"]);
    const sortFields = ref(["name", "startDate", "endDate", "status"]);

    onMounted(() => {
      if (!estates.value.length) {
        store.dispatch("estate/getEstates", company.value.id);
      }
      if (!tasks.value.length) {
        store.dispatch("task/getTasks", user.value.companyId);
      }
    });

    return {
      closeToDeadline,
      company,
      estates,
      fields,
      getEstateName,
      getLocaleDate,
      locale,
      PlusIcon,
      router,
      searchFields,
      sortFields,
      t,
      tasks,
      waiting,
    };
  },
};
</script>
